<template>
    <div class="information-overlay" :class="{ active: !instagramReplyMode && (status || isReplyingToComment) }" ref="informationOverlay">
        <div class="description" v-if="!instagramReplyMode && !showingInstagramCommentButtons">
            <EnterSvg v-if="status === 'enter-join'" />
            <EnterSvg v-if="status === 'enter-create'" />
            <div class="material-icons" v-if="status === 'inactive'">block</div>
            <div class="material-icons" v-if="status === 'deactive-user'">chat</div>
            <span>{{ statusText[status] }}</span>
        </div>
        <div v-if="!instagramReplyMode && channelStatus && !showingInstagramCommentButtons">
            <div class="button-group" v-if="['enter-join', 'enter-create'].includes(status)">
                <SupsisButton @click="clickButton" primary>
                    {{ joinText() }}
                </SupsisButton>
                <SupsisButton 
                     v-if="status === 'enter-create' && isWhatsapp && whatsappChannels.length > 1"
                     v-tippy="{ followCursor: false, placement: 'top' }"
                     :content="$t('message.startChatWithDifferentChannel')"
                     @click="startWhatsappChat"
                     icon="swap_horiz"
                     square
                     ghost
                >
                </SupsisButton>
                <SupsisButton 
                     v-if="canToggleAnswerStatus"
                     v-tippy="{ followCursor: false, placement: 'top' }"
                     :content="answerStatusButtonText"
                     @click="toggleAnswerStatus"
                     :icon="answerStatusIcon"
                     square
                     ghost
                     :loading="markingAsAnswered"
                >
                </SupsisButton>
            </div>
        </div>
        <SupsisButton @click="sendTemplateMessage" v-if="!instagramReplyMode && ['deactive-user'].includes(status) && isWhatsapp && !showingInstagramCommentButtons" primary>
            {{ $t('message.sendTemplateMessage') }}
        </SupsisButton>
        
        <!-- Instagram/Messenger: Reply to comment VEYA (Comment var + 24 saat dolmuş) -->
        <div v-if="!instagramReplyMode && showingInstagramCommentButtons" class="instagram-comment-buttons">
            <div class="button-row">
                <SupsisButton @click="replyCommentAsDM" primary :disabled="isLastCommentDeleted" v-if="!isLastCommentRepliedViaDM">
                    DM'den Yanıtla
                </SupsisButton>
                <SupsisButton @click="replyComment" ghost :disabled="isLastCommentDeleted">
                    Yorumu Yanıtla
                </SupsisButton>
            </div>
            <div class="button-row" v-if="contact.platform === 'instagram'">
                <SupsisButton @click="toggleHideComment" ghost v-if="!isLastCommentDeleted">
                    {{ isLastCommentHidden ? 'Yorumu Göster' : 'Yorumu Gizle' }}
                </SupsisButton>
                <SupsisButton @click="deleteComment" ghost danger v-if="!isLastCommentDeleted">
                    Yorumu Sil
                </SupsisButton>
            </div>
            <div v-if="isLastCommentDeleted" class="comment-deleted-warning">
                ⚠️ Bu yorum silindi
            </div>
        </div>
        
        <!-- Instagram/Messenger: 24 saat doldu (Direct mesaj için) - lastMessageTS varsa -->
        <div v-if="!instagramReplyMode && ['deactive-user'].includes(status) && isInstagramOrMessenger && !isReplyingToComment && !isLastMessageComment && hasLastMessageTS" class="instagram-waiting-message">
            <span>{{ instagramWaitingMessage }}</span>
        </div>
        <Modal2 v-if="showWhatsappChatStarterModal" :show-footer="false" class="modal-wp-chat-starter"
            :title="$t('message.whatsappChatStarter')" @cancel="() => (showWhatsappChatStarterModal = false)">
            <WhatsappChatStarter :default-phone-number="contact.whatsappNumber" channel-override phone-number-override
                @close="() => (showWhatsappChatStarterModal = false)" :default-email="contact.email"
                :default-name="contact.name || contact.fullname" />
        </Modal2>
    </div>
</template>

<script>
import { log2 } from "../../../../../utils/log2";
import { mapActions, mapState } from "vuex";
import EnterSvg from "../../svg/EnterSvg.vue";
import SupsisButton from "../../../../../views/Pages/Settings/components/SupsisButton.vue";
import Modal2 from "../../../../common/Modal2.vue";
import WhatsappChatStarter from "../../../../../views/Pages/Integrations/meta-leads/WhatsappChatStarter.vue";
import _ from "lodash";

const log = log2("InformationOverlay");
export default {
    name: "InformationOverlay",
    components: { WhatsappChatStarter, Modal2, SupsisButton, EnterSvg },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
        status: {
            type: String,
            default: null,
        },
        instagramReplyMode: {
            type: String,
            default: null,
        },
        replyToMessage: {
            type: Object,
            default: null,
        },
    },
    computed: {
        ...mapState({
            whatsappChannels: (state) => state.channel.channels.filter((x) => x.type.startsWith("whatsapp")),
            departments: (state) => state.subject.subjects.data,
            site: (state) => state.site.site,
        }),
        channelStatus() {
            if (this.contact.platform === "whatsapp") {
                return this.whatsappChannels.some((channel) => channel._id === this.contact.channel);
            }
            return true;
        },
        statusText() {
            return {
                "enter-join": this.$t('message.pressEnterToJoin'),
                "enter-create": this.$t('message.pressEnterToStartChat'),
                "inactive": this.$t('message.cannotSendMessage'),
                "deactive-user": this.$t('message.cannotSendSessionMessage'),
            }
        },
        isUnanswered() {
            const isUnanswered = this.contact.answerStatus === "unanswered";
            const isDestroyed = _.get(this.contact, "lastConversation.status") === "destroyed";
            return isUnanswered && isDestroyed;
        },
        canToggleAnswerStatus() {
            const isDestroyed = _.get(this.contact, "lastConversation.status") === "destroyed";
            return isDestroyed;
        },
        answerStatusButtonText() {
            return this.contact.answerStatus === "answered" 
                ? "Cevaplanmadı Olarak İşaretle (C)" 
                : "Cevaplandı Olarak İşaretle (C)";
        },
        answerStatusIcon() {
            return this.contact.answerStatus === "answered" ? "close" : "done";
        },
        isReplyingToComment() {
            if (!this.replyToMessage) return false;
            
            const platform = this.contact?.platform;
            if (!["instagram", "messenger"].includes(platform)) return false;
            
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            const lastCommentId = _.get(this.contact, `${infoField}.lastCommentId`);
            const replyMessageId = _.get(this.replyToMessage, "raw.externalProviderMessageId") || 
                                   _.get(this.replyToMessage, "raw.metaMessageId") ||
                                   _.get(this.replyToMessage, "raw.payload.metaMessageId");
            
            console.log("isReplyingToComment debug", {
                lastCommentId,
                replyMessageId,
                matches: lastCommentId === replyMessageId,
                replyToMessage: this.replyToMessage
            });
            
            // Reply to message yapıldı VE o mesaj son comment ise
            return lastCommentId && replyMessageId && lastCommentId === replyMessageId;
        },
        isWhatsapp() {
            return ["whatsapp", "meta-leads"].includes(this.contact?.platform);
        },
        isInstagramOrMessenger() {
            return ["instagram", "messenger"].includes(this.contact?.platform);
        },
        isLastMessageComment() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            const lastMessageSource = _.get(this.contact, `${infoField}.lastMessageSource`);
            return lastMessageSource === "comment";
        },
        isLastCommentReplied() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            const lastCommentReplied = _.get(this.contact, `${infoField}.lastCommentReplied`);
            return lastCommentReplied === true;
        },
        instagramWaitingMessage() {
            return "Bu kişiye mesaj gönderemezsiniz. 24 saatlik süre doldu, müşteriden yanıt bekleniyor.";
        },
        isLastCommentDeleted() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            return _.get(this.contact, `${infoField}.lastCommentDeleted`) === true;
        },
        isLastCommentHidden() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            return _.get(this.contact, `${infoField}.lastCommentHidden`) === true;
        },
        isLastCommentRepliedViaDM() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            return _.get(this.contact, `${infoField}.lastCommentRepliedViaDM`) === true;
        },
        hasLastMessageTS() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            const lastMessageTS = _.get(this.contact, `${infoField}.lastMessageTS`);
            return !!lastMessageTS;
        },
        isIn24Hours() {
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            const lastMessageTS = _.get(this.contact, `${infoField}.lastMessageTS`);
            if (!lastMessageTS) return false;
            return Date.now() - new Date(lastMessageTS).getTime() < 24 * 60 * 60 * 1000;
        },
        isUserInConversation() {
            const authUser = this.$store.state.auth.user;
            const conversationUsers = _.get(this.contact, 'lastConversation.users', []);
            return conversationUsers.includes(authUser._id);
        },
        showingInstagramCommentButtons() {
            console.log("showingInstagramCommentButtons - status:", this.status, "isReplyingToComment:", this.isReplyingToComment);
            
            // Sohbet kapalıysa Instagram butonları gösterme
            if (this.status === 'enter-create') {
                console.log("showingInstagramCommentButtons: false (enter-create)");
                return false;
            }
            
            if (!this.isInstagramOrMessenger) return false;
            
            // Reply yapıldıysa her zaman göster (24 saat veya lastMessageComment önemli değil)
            if (this.isReplyingToComment) {
                console.log("showingInstagramCommentButtons: true (isReplyingToComment)");
                return true;
            }
            
            // Reply yoksa, comment olmalı
            if (!this.isLastMessageComment) {
                console.log("showingInstagramCommentButtons: false (no lastMessageComment)");
                return false;
            }
            
            // 24 saat içindeyse gösterme
            if (this.isIn24Hours) {
                console.log("showingInstagramCommentButtons: false (in 24 hours)");
                return false;
            }
            
            // 24 saat dolmuşsa göster
            console.log("showingInstagramCommentButtons: true (24 hours passed)");
            return true;
        },
    },

    data: () => ({
        handleEnterKey: null,
        showWhatsappChatStarterModal: false,
        markingAsAnswered: false,
    }),
    mounted() {
        this.$emit("blur-editor");
        this.handleEnterKey = (e) => {
            const isInput = _.get(e, "target.nodeName") === "INPUT";
            const isTextArea = _.get(e, "target.nodeName") === "TEXTAREA";
            const editableContent = !!e?.target?.getAttribute("contenteditable");
            if (isInput || isTextArea || editableContent) {
                return;
            }
            if (e.key === "Enter" && this.status === "enter-join") {
                this.clickButton();
            } else if (e.key === "Enter" && this.status === "enter-create") {
                this.clickButton();
            }
        };
        document.addEventListener("keydown", this.handleEnterKey);
    },
    watch: {
        isReplyingToComment(newVal) {
            console.log("isReplyingToComment changed:", newVal);
            if (newVal) {
                this.$emit("blur-editor");
            }
        },
        showingInstagramCommentButtons(newVal) {
            console.log("showingInstagramCommentButtons changed:", newVal);
        },
    },
    methods: {
        ...mapActions({
            joinContactConversation: "chat/joinContactConversation",
            startChat: "chat/startChat",
            updateContactFields: "contact/updateContactFields",
            updateVisitorAttr: "visitor/updateVisitorAttr",
        }),
        joinText() {
            if (this.status === "enter-join") {
                const isChatbot = _.get(this.contact, "lastConversation.status") === "chatbot";
                return isChatbot ? this.$t("message.takeControlFromChatbot") : this.$t("message.joinChat");
            } else if (this.status === "enter-create") {
                return this.$t("wp.startChatWp");
            }
        },
        sendTemplateMessage() {
            this.$emit("send-template-message");
        },
        clickButton() {
            if (this.status === "enter-join") {
                const authUser = this.$store.state.auth.user;
                const isUserRoot = _.get(authUser, "role.isRoot", false);
                const channels = this.$store.state.channel.channels;
                const contactChannel = channels.find((c) => c._id === this.contact.channel);
                const isChannelOwner = contactChannel && (contactChannel?.visibility?.owners || []).includes(authUser._id);
                
                // Eğer kullanıcı root ise VE kanal owner ise departman kontrolü yapma
                const skipDepartmentCheck = isUserRoot || isChannelOwner;
                
                if (!skipDepartmentCheck) {
                    const lastConversationDepartment = _.get(this.contact, "lastConversation.subject");
                    let myDepartments = this.$store.state.auth.user?.subjects || [];
                    if (Array.isArray(myDepartments) && myDepartments.length === 0) {
                        myDepartments = this.departments.map((x) => x._id);
                    }
                    myDepartments = myDepartments.map((x) => (typeof x === "string" ? x : x._id));
                    const departmentForceEnabled = !!this.$store.state.setting?.settings?.subject;
                    if (
                        departmentForceEnabled &&
                        lastConversationDepartment &&
                        !myDepartments.includes(lastConversationDepartment) &&
                        myDepartments.length > 0
                    ) {
                        this.$message.error(this.$t('message.conversationNotInYourDepartments'));
                        return;
                    }
                }
                
                this.joinContactConversation(this.contact);
            } else if (this.status === "enter-create") {
                this.startChat(this.contact);
            }
            this.$emit("focus-editor");
        },
        replyCommentAsDM() {
            if (this.isLastCommentDeleted) {
                this.$message.error("Bu yorum silindi, yanıt veremezsiniz.");
                return;
            }
            this.$emit("reply-comment-as-dm");
        },
        getCommentId() {
            // Önce reply to message'dan al
            if (this.replyToMessage) {
                const replyCommentId = _.get(this.replyToMessage, "raw.externalProviderMessageId") || _.get(this.replyToMessage, "raw.metaMessageId");
                if (replyCommentId) return replyCommentId;
            }
            
            // Yoksa contact'tan al
            const platform = this.contact?.platform;
            const infoField = platform === "instagram" ? "instagramInfo" : "messengerInfo";
            return _.get(this.contact, `${infoField}.lastCommentId`);
        },
        replyComment() {
            if (this.isLastCommentDeleted) {
                this.$message.error("Bu yorum silindi, yanıt veremezsiniz.");
                return;
            }
            this.$emit("reply-comment");
        },
        toggleHideComment() {
            const commentId = this.getCommentId();
            const hide = !this.isLastCommentHidden; // Toggle
            
            if (commentId) {
                this.$emit("toggle-hide-comment", { commentId, hide });
            } else {
                this.$message.error("Comment ID bulunamadı");
            }
        },
        deleteComment() {
            const commentId = this.getCommentId();
            
            if (commentId) {
                this.$emit("delete-comment", { commentId });
            } else {
                this.$message.error("Comment ID bulunamadı");
            }
        },
        startWhatsappChat() {
            if (this.contact.whatsappNumber) {
                this.showWhatsappChatStarterModal = true;
            } else {
                this.$message.error(this.$t('message.phoneNumberNotAvailable'));
            }
        },
        async toggleAnswerStatus() {
            if (this.markingAsAnswered) return;
            
            const newStatus = this.contact.answerStatus === "answered" ? "unanswered" : "answered";
            const successMessage = newStatus === "answered" 
                ? "Cevaplandı olarak işaretlendi" 
                : "Cevaplanmadı olarak işaretlendi";
            
            try {
                this.markingAsAnswered = true;
                
                this.updateVisitorAttr({
                    publishSocket: false,
                    visitorId: this.contact._id,
                    payload: {
                        answerStatus: newStatus,
                    },
                });
                
                // Store'u güncelle
                this.updateContactFields({
                    contactId: this.contact._id,
                    set: {
                        answerStatus: newStatus,
                    },
                });
                
                // Local contact objesini de güncelle
                this.$set(this.contact, 'answerStatus', newStatus);
                
                this.$message.success(successMessage);
            } catch (error) {
                this.$message.error("İşlem başarısız oldu");
                log.error("toggleAnswerStatus error", error);
            } finally {
                this.markingAsAnswered = false;
            }
        },
    },
    beforeDestroy() {
        if (this.handleEnterKey) {
            document.removeEventListener("keydown", this.handleEnterKey);
        }
    },
};
</script>
<style lang="scss">
.information-overlay {
    &.active {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background: rgba(255, 255, 255, 0.63);
        z-index: 4;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 18px;
        color: black;
        justify-content: center;
        flex-direction: column;

        .description {
            display: flex;
            align-items: center;
            gap: 4px;
            justify-content: center;
        }

        .button-group {
            display: flex;
            align-items: center;
            gap: 8px;
            justify-content: center;
        }
        
        .instagram-comment-buttons {
            display: flex;
            flex-direction: column;
            gap: 12px;
            align-items: center;
            z-index: 10;
            position: relative;
            
            .button-row {
                display: flex;
                gap: 8px;
                align-items: center;
            }
        }
        
        .instagram-waiting-message {
            text-align: center;
            padding: 12px 24px;
            background: rgba(255, 193, 7, 0.1);
            border-radius: 8px;
            color: #856404;
            font-size: 14px;
        }
        
        .comment-deleted-warning {
            text-align: center;
            padding: 12px 24px;
            background: rgba(220, 53, 69, 0.1);
            border-radius: 8px;
            color: #721c24;
            font-size: 14px;
            font-weight: 500;
        }
    }
}
</style>
